import { z } from 'zod';

// AuthType values mirror the backend enum. Phase 1 adds two MCP-friendly OAuth flows
// (oauth2_cimd, oauth2_client_credentials) alongside the existing 5 + "not_configured".
// auth-config-schemas.ts owns the full AuthConfig discriminator (extended in T1.14);
// this enum is the lighter-weight version stamped on SystemRecord/SystemPayload.
export const AuthTypeSchema = z.enum([
  "api_key",
  "basic_auth",
  "bearer_token",
  "oauth2",
  "oauth2_cimd",
  "oauth2_client_credentials",
  "none",
  "not_configured",
]);
export type AuthType = z.infer<typeof AuthTypeSchema>;

// Base schemas for system entities
export const SystemRecordSchema = z.object({
  system_id: z.string().min(1, "System ID is required"),
  name: z.string().min(1, "System name is required"),
  description: z.string().optional(),
  system_type: z.string().optional(),
  spec_count: z.number().int().min(0).optional(),
  api_spec_count: z.number().int().min(0).optional(),
  created_time: z.string().datetime().optional(),
  last_updated_time: z.string().datetime().optional(),
  last_updated: z.string().datetime().optional(),
  status: z.string().optional(), // Original status from database
  system_status: z.enum(["active", "draft", "needs_auth"]).optional(), // Calculated server-side
  auth_type: AuthTypeSchema.optional(),
  auth_status: z.enum(["valid", "not_configured", "missing_credentials", "invalid"]).optional(), // Granular auth status
  ai_summary: z.string().optional(), // AI-generated summary of the system
  has_shared_auth: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
  is_template: z.boolean().optional(),
  is_owner: z.boolean().optional(), // True when the system belongs to the current workspace (own)
  read_only: z.boolean().optional(), // BE serve-time flag: true → published template the caller does not own (hide edit/add/delete)
  icon_url: z.string().optional(),
});

export type SystemRecord = z.infer<typeof SystemRecordSchema>;

export const OauthInfoSchema = z.object({
  system_id: z.string().min(1, "System ID is required"),
  callback_url: z.string().url("Invalid callback URL"),
});

export type OauthInfo = z.infer<typeof OauthInfoSchema>;

export const SystemPayloadSchema = z.object({
  name: z.string().min(1, "System name is required").max(255, "System name must be 255 characters or less"),
  description: z.string().max(1000, "Description must be 1000 characters or less").optional(),
  system_type: z.string().max(255, "System type must be 255 characters or less").optional(),
});

export type SystemPayload = z.infer<typeof SystemPayloadSchema>;

export const SystemUpdatePayloadSchema = z.object({
  name: z.string().min(1, "System name is required").max(255, "System name must be 255 characters or less").optional(),
  description: z.string().max(1000, "Description must be 1000 characters or less").optional(),
  ai_summary: z.string().optional(),
  status: z.enum(["active", "inactive", "archived"]).optional(),
});

export type SystemUpdatePayload = z.infer<typeof SystemUpdatePayloadSchema>;

export const SystemSortFieldSchema = z.enum(['name', 'last_updated', 'api_count']);
export type SystemSortField = z.infer<typeof SystemSortFieldSchema>;

export const SortOrderSchema = z.enum(['asc', 'desc']);
export type SortOrder = z.infer<typeof SortOrderSchema>;

export const ListSystemsParamsSchema = z.object({
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(100).optional().default(9),
  query: z.string().optional(),
  sortField: SystemSortFieldSchema.optional(),
  sortOrder: SortOrderSchema.optional(),
});

export type ListSystemsParams = z.infer<typeof ListSystemsParamsSchema>;

// Response schemas
export const PaginatedSystemsResponseSchema = z.object({
  systems: z.array(SystemRecordSchema),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
});

export type PaginatedSystemsResponse = z.infer<typeof PaginatedSystemsResponseSchema>;

// API Response wrapper
export const ApiResponseSchema = <T extends z.ZodType>(dataSchema: T) => z.object({
  status: z.enum(['OK', 'ERROR']),
  data: z.array(dataSchema).optional(),
  message: z.string().optional(),
  total: z.number().int().min(0).optional(),
  page: z.number().int().min(1).optional(),
  pageSize: z.number().int().min(1).optional(),
});

export type ApiResponse<T> = {
  status: 'OK' | 'ERROR';
  data?: T[];
  message?: string;
  total?: number;
  page?: number;
  pageSize?: number;
};
